import { useMemo, useRef, useState, useCallback } from 'react';
import { Button, Toggle } from '../common';
import { useBannerStore } from '../../stores';
import { BACKGROUND_PRESETS } from '../../constants';
import { isBackgroundLayer, isFrameLayer } from '../../utils/layerUtils';

function DragHandleIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <circle cx="6" cy="5.5" r="1.25" />
      <circle cx="6" cy="10" r="1.25" />
      <circle cx="6" cy="14.5" r="1.25" />
      <circle cx="14" cy="5.5" r="1.25" />
      <circle cx="14" cy="10" r="1.25" />
      <circle cx="14" cy="14.5" r="1.25" />
    </svg>
  );
}

export function BannerSidebar() {
  const {
    elements,
    selectedElementId,
    selectElement,
    removeElement,
    updateElementScale,
    updateElementRotation,
    updateElementLayers,
    updateElementVerticalOffsets,
    bringToFront,
    reorderElements,
    backgroundImage,
    setBackgroundImage,
  } = useBannerStore();

  const bgInputRef = useRef<HTMLInputElement>(null);
  const [draggingElementId, setDraggingElementId] = useState<string | null>(null);
  const [dragOverElementId, setDragOverElementId] = useState<string | null>(null);

  const selectedElement = elements.find((e) => e.id === selectedElementId);
  const sortedElements = [...elements].sort((a, b) => b.zIndex - a.zIndex);

  // Check if selected element has background layers
  const hasBackgroundLayers = useMemo(() => {
    if (!selectedElement) return false;
    return selectedElement.nftData.layerAssets.some(isBackgroundLayer);
  }, [selectedElement]);

  // Check if selected element has frame layers
  const hasFrameLayers = useMemo(() => {
    if (!selectedElement) return false;
    return selectedElement.nftData.layerAssets.some(isFrameLayer);
  }, [selectedElement]);

  const handleBackgroundToggle = (checked: boolean) => {
    if (!selectedElement) return;
    updateElementLayers(
      selectedElement.id,
      selectedElement.selectedLayers,
      checked,
      selectedElement.useFrameLayer
    );
  };

  const handleFrameToggle = (checked: boolean) => {
    if (!selectedElement) return;
    updateElementLayers(
      selectedElement.id,
      selectedElement.selectedLayers,
      selectedElement.useBackgroundLayer,
      checked
    );
  };

  const isCustomBackground = useMemo(() => {
    if (!backgroundImage) return false;
    return !BACKGROUND_PRESETS.some((preset) => preset.url && preset.url === backgroundImage);
  }, [backgroundImage]);

  const activeBackgroundId = useMemo(() => {
    if (!backgroundImage) return null;
    const preset = BACKGROUND_PRESETS.find((item) => item.url && item.url === backgroundImage);
    return preset?.id || 'upload';
  }, [backgroundImage]);

  const handleBackgroundSelect = (presetId: string) => {
    const preset = BACKGROUND_PRESETS.find((item) => item.id === presetId);
    if (!preset) return;
    if (preset.isUpload) {
      bgInputRef.current?.click();
      return;
    }
    setBackgroundImage(preset.url);
  };

  const handleBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setBackgroundImage(dataUrl);
    };
    reader.readAsDataURL(file);

    e.target.value = '';
  };

  const clearDragState = useCallback(() => {
    setDraggingElementId(null);
    setDragOverElementId(null);
  }, []);

  const handleElementDragStart = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>, elementId: string) => {
      e.preventDefault();
      e.stopPropagation();
      setDraggingElementId(elementId);
      setDragOverElementId(elementId);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    []
  );

  const handleElementDragMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!draggingElementId) return;
      const target = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)
        ?.closest('[data-banner-element-id]') as HTMLElement | null;
      const targetId = target?.dataset.bannerElementId;
      if (!targetId || targetId === draggingElementId) return;
      setDragOverElementId(targetId);
      reorderElements(draggingElementId, targetId);
    },
    [draggingElementId, reorderElements]
  );

  const handleElementDragEnd = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      clearDragState();
    },
    [clearDragState]
  );

  return (
    <div className="w-full bg-white dark:bg-neutral-900 rounded-xl border border-surface-border dark:border-neutral-800 flex flex-col max-h-[450px] lg:max-h-[calc(100dvh-200px)] overflow-hidden">
      {/* Header - Fixed */}
      <div className="p-4 border-b border-surface-border dark:border-neutral-800 flex-shrink-0">
        <h3 className="text-sm font-semibold text-text-dark dark:text-neutral-100">Elements</h3>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y p-4 space-y-4">
        {/* Backgrounds */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-text-muted dark:text-neutral-400">Backgrounds</p>
            <input
              ref={bgInputRef}
              type="file"
              accept="image/*"
              onChange={handleBackgroundUpload}
              className="hidden"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-2 px-2">
            {BACKGROUND_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => handleBackgroundSelect(preset.id)}
                className={`relative w-11 h-11 flex-shrink-0 rounded-md border-2 overflow-hidden transition-all ${
                  activeBackgroundId === preset.id
                    ? 'border-game-blue ring-2 ring-game-blue/50'
                    : 'border-surface-border dark:border-neutral-700 hover:border-game-blue/50'
                }`}
                title={preset.name}
              >
                {preset.isUpload ? (
                  <div className="w-full h-full bg-surface-secondary dark:bg-neutral-800 flex flex-col items-center justify-center gap-1">
                    {isCustomBackground ? (
                      <img src={backgroundImage || ''} alt="Custom" className="w-full h-full object-cover" />
                    ) : (
                      <>
                        <svg className="w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-[8px] text-text-muted">Upload</span>
                      </>
                    )}
                  </div>
                ) : (
                  <img src={preset.thumbnail} alt={preset.name} className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Elements List */}
        <div>
          {elements.length === 0 ? (
            <div className="bg-surface-secondary dark:bg-neutral-800/50 rounded-lg p-6 text-center">
              <p className="text-sm text-text-muted dark:text-neutral-400">
                No NFTs added yet.
              </p>
              <p className="text-xs text-text-muted dark:text-neutral-500 mt-1">
                Click "Add NFT" to start creating your banner.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedElements.map((element) => (
                <div
                  key={element.id}
                  data-banner-element-id={element.id}
                  onClick={() => {
                    if (draggingElementId) return;
                    selectElement(element.id);
                    bringToFront(element.id);
                  }}
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                    draggingElementId === element.id
                      ? 'opacity-70 border-game-blue/70 bg-game-blue/10'
                      : dragOverElementId === element.id
                        ? 'border-game-blue/60 bg-game-blue/5'
                        : ''
                  } ${
                    selectedElementId === element.id
                      ? 'bg-game-blue/10 border border-game-blue shadow-sm'
                      : 'bg-surface-secondary dark:bg-neutral-800 hover:bg-surface dark:hover:bg-neutral-700 border border-transparent'
                  }`}
                >
                  <img
                    src={element.nftData.imageUrl}
                    alt={element.nftData.name}
                    className="w-10 h-10 rounded object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-dark dark:text-neutral-200 truncate">
                      {element.nftData.name}
                    </p>
                    <p className="text-xs text-text-muted dark:text-neutral-400">
                      z: {element.zIndex} | scale: {element.scale.toFixed(2)}
                    </p>
                  </div>
                  <button
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => handleElementDragStart(e, element.id)}
                    onPointerMove={handleElementDragMove}
                    onPointerUp={handleElementDragEnd}
                    onPointerCancel={handleElementDragEnd}
                    className="p-1.5 rounded-lg border border-transparent text-text-muted/60 hover:text-text-dark dark:hover:text-neutral-200 hover:bg-white/80 dark:hover:bg-neutral-800/80 hover:border-surface-border dark:hover:border-neutral-700 transition-all cursor-grab active:cursor-grabbing touch-none"
                    title="Drag to reorder"
                    aria-label={`Drag to reorder ${element.nftData.name}`}
                  >
                    <DragHandleIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected Element Properties */}
        {selectedElement && (
          <>
            <div className="h-px bg-surface-border dark:border-neutral-800" />

            <div>
              <h4 className="text-sm font-semibold text-text-dark dark:text-neutral-100 mb-3">
                {selectedElement.nftData.name.length > 25
                  ? selectedElement.nftData.name.slice(0, 22) + '...'
                  : selectedElement.nftData.name}
              </h4>

              <div className="space-y-4">
                {/* Scale Control */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-text-muted dark:text-neutral-400">Scale</label>
                    <span className="text-xs font-mono text-text-dark dark:text-neutral-300">
                      {selectedElement.scale.toFixed(2)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="2"
                    step="0.05"
                    value={selectedElement.scale}
                    onChange={(e) =>
                      updateElementScale(selectedElement.id, parseFloat(e.target.value))
                    }
                    className="w-full h-1.5 bg-surface-secondary dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-game-blue"
                  />
                </div>

                {/* Rotation Control */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-text-muted dark:text-neutral-400">Rotation</label>
                    <span className="text-xs font-mono text-text-dark dark:text-neutral-300">
                      {selectedElement.rotation}°
                    </span>
                  </div>
                  <input
                    type="range"
                    min="-180"
                    max="180"
                    step="5"
                    value={selectedElement.rotation}
                    onChange={(e) =>
                      updateElementRotation(selectedElement.id, parseInt(e.target.value))
                    }
                    className="w-full h-1.5 bg-surface-secondary dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-game-blue"
                  />
                </div>

                {/* NFT Layer Toggles */}
                {hasBackgroundLayers && (
                  <div className="flex items-center justify-between rounded-lg bg-surface-secondary dark:bg-neutral-800/50 px-3 py-2.5">
                    <div className="flex-1">
                      <span className="text-xs font-medium text-text-dark dark:text-neutral-200">Background</span>
                      <p className="text-[10px] text-text-muted dark:text-neutral-500 mt-0.5">Toggle NFT background</p>
                    </div>
                    <Toggle
                      checked={selectedElement.useBackgroundLayer}
                      onChange={handleBackgroundToggle}
                    />
                  </div>
                )}

                {hasFrameLayers && (
                  <div className="flex items-center justify-between rounded-lg bg-surface-secondary dark:bg-neutral-800/50 px-3 py-2.5">
                    <div className="flex-1">
                      <span className="text-xs font-medium text-text-dark dark:text-neutral-200">Frame</span>
                      <p className="text-[10px] text-text-muted dark:text-neutral-500 mt-0.5">Toggle NFT frame</p>
                    </div>
                    <Toggle
                      checked={selectedElement.useFrameLayer}
                      onChange={handleFrameToggle}
                    />
                  </div>
                )}

                {/* Background Vertical Position */}
                {hasBackgroundLayers && selectedElement.useBackgroundLayer && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-text-muted dark:text-neutral-400">Background Y</label>
                      <span className="text-xs font-mono text-text-dark dark:text-neutral-300">
                        {selectedElement.backgroundVerticalOffset || 0}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="-100"
                      max="100"
                      step="1"
                      value={selectedElement.backgroundVerticalOffset || 0}
                      onChange={(e) =>
                        updateElementVerticalOffsets(
                          selectedElement.id,
                          parseInt(e.target.value),
                          selectedElement.frameVerticalOffset || 0
                        )
                      }
                      className="w-full h-1.5 bg-surface-secondary dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-game-blue"
                    />
                  </div>
                )}

                {/* Frame Vertical Position */}
                {hasFrameLayers && selectedElement.useFrameLayer && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-text-muted dark:text-neutral-400">Frame Y</label>
                      <span className="text-xs font-mono text-text-dark dark:text-neutral-300">
                        {selectedElement.frameVerticalOffset || 0}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="-100"
                      max="100"
                      step="1"
                      value={selectedElement.frameVerticalOffset || 0}
                      onChange={(e) =>
                        updateElementVerticalOffsets(
                          selectedElement.id,
                          selectedElement.backgroundVerticalOffset || 0,
                          parseInt(e.target.value)
                        )
                      }
                      className="w-full h-1.5 bg-surface-secondary dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-game-blue"
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeElement(selectedElement.id)}
                    className="w-full text-xs text-accent-error hover:bg-accent-error/10"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
